import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { ProtectedRoute, SuperAdminRoute, FirmAdminRoute, CoachRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { CollegeAdminDashboard } from './pages/CollegeAdminDashboard';
import { AccountFormPage } from './pages/AccountFormPage';
import { AccountDetailPage } from './pages/AccountDetailPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { FirmDashboard } from './pages/firm/FirmDashboard';
import { FirmRoster } from './pages/firm/FirmRoster';
import { FirmCoaches } from './pages/firm/FirmCoaches';
import { FirmSeats } from './pages/firm/FirmSeats';
import { FirmClients } from './pages/firm/FirmClients';
import { ClientReport } from './pages/firm/ClientReport';
import { CoachCaseload } from './pages/coach/CoachCaseload';
import { CandidateDetail } from './pages/coach/CandidateDetail';
import { FirmsListPage } from './pages/FirmsListPage';
import { FirmFormPage } from './pages/FirmFormPage';
import { OutreachListPage } from './pages/outreach/OutreachListPage';
import { NewCampaignPage } from './pages/outreach/NewCampaignPage';
import { CampaignDetailPage } from './pages/outreach/CampaignDetailPage';
import { CoachInvitePage } from './pages/CoachInvitePage';
import { CoachSignupPage } from './pages/CoachSignupPage';
import { CoachApplicationsPage } from './pages/CoachApplicationsPage';

const LAST_PORTAL_KEY = 'klarr_last_portal';

// Routes to role-specific home on /dashboard
function DashboardRouter() {
  const { user, collegeAdmin, firmAdmin, coach } = useAuth();
  if (firmAdmin && !user) {
    // Dual-role: honour stored preference, default to firm
    if (coach && localStorage.getItem(LAST_PORTAL_KEY) === 'coach') {
      return <Navigate to="/coach/caseload" replace />;
    }
    return <Navigate to="/firm/dashboard" replace />;
  }
  if (coach && !user) return <Navigate to="/coach/caseload" replace />;
  if (collegeAdmin && !user) return <CollegeAdminDashboard />;
  return <DashboardPage />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/new"
            element={
              <SuperAdminRoute>
                <AccountFormPage />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/accounts/:id/edit"
            element={
              <SuperAdminRoute>
                <AccountFormPage />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/accounts/:id"
            element={
              <SuperAdminRoute>
                <AccountDetailPage />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/users"
            element={
              <SuperAdminRoute>
                <UserManagementPage />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/firms"
            element={
              <SuperAdminRoute>
                <FirmsListPage />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/firms/new"
            element={
              <SuperAdminRoute>
                <FirmFormPage />
              </SuperAdminRoute>
            }
          />

          {/* Firm admin portal */}
          <Route
            path="/firm/dashboard"
            element={
              <FirmAdminRoute>
                <FirmDashboard />
              </FirmAdminRoute>
            }
          />
          <Route
            path="/firm/roster"
            element={
              <FirmAdminRoute>
                <FirmRoster />
              </FirmAdminRoute>
            }
          />
          <Route
            path="/firm/coaches"
            element={
              <FirmAdminRoute>
                <FirmCoaches />
              </FirmAdminRoute>
            }
          />
          <Route
            path="/firm/seats"
            element={
              <FirmAdminRoute>
                <FirmSeats />
              </FirmAdminRoute>
            }
          />
          <Route
            path="/firm/clients"
            element={
              <FirmAdminRoute>
                <FirmClients />
              </FirmAdminRoute>
            }
          />
          <Route
            path="/firm/clients/:clientId/report"
            element={
              <FirmAdminRoute>
                <ClientReport />
              </FirmAdminRoute>
            }
          />

          {/* Outreach */}
          <Route
            path="/outreach"
            element={
              <SuperAdminRoute>
                <OutreachListPage />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/outreach/new"
            element={
              <SuperAdminRoute>
                <NewCampaignPage />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/outreach/:campaignId"
            element={
              <SuperAdminRoute>
                <CampaignDetailPage />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/coach-applications"
            element={
              <SuperAdminRoute>
                <CoachApplicationsPage />
              </SuperAdminRoute>
            }
          />

          {/* Coach portal */}
          <Route
            path="/coach/caseload"
            element={
              <CoachRoute>
                <CoachCaseload />
              </CoachRoute>
            }
          />
          <Route
            path="/coach/candidate/:uid"
            element={
              <CoachRoute>
                <CandidateDetail />
              </CoachRoute>
            }
          />

          <Route path="/coach-invite/:candidateUid" element={<CoachInvitePage />} />
          <Route path="/coach-signup" element={<CoachSignupPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
